import { formatSendTrace, type SendTraceSnapshot } from "@extension/wa-communication-layer/message/send-stage-trace";

/**
 * #503: follow a timed-out send to its PAGE-side outcome.
 *
 * When the 180s guard fires, the late-settlement observer watches the IPC call,
 * which has its own ceiling: it gives up ("ipc-timeout") while WhatsApp's send is
 * often still running page-side. The real fate (delivered late, or nacked with
 * `isSendFailure`) then lands only on the page trace, which nothing reads again —
 * the next retry reads its OWN trace. This polls the timed-out attempt's trace,
 * pinned by its `startedAt`, until that attempt ends.
 */
export const LATE_PAGE_OUTCOME_POLL_MS = 15_000;
export const LATE_PAGE_OUTCOME_MAX_MS = 10 * 60_000;

export type LatePageOutcome =
    | { kind: 'ended'; snap: SendTraceSnapshot }
    /** The page dropped the trace (history cap) before the attempt ended. */
    | { kind: 'gone'; last: SendTraceSnapshot | null }
    /** Still running when we stopped looking. */
    | { kind: 'still-open'; last: SendTraceSnapshot };

export async function awaitPageOutcome(
    fetchSnap: () => Promise<SendTraceSnapshot | null>,
    opts: { pollMs?: number; maxMs?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<LatePageOutcome> {
    const pollMs = opts.pollMs ?? LATE_PAGE_OUTCOME_POLL_MS;
    const maxMs = opts.maxMs ?? LATE_PAGE_OUTCOME_MAX_MS;
    const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    let last: SendTraceSnapshot | null = null;
    for (let waited = 0; ; waited += pollMs) {
        const snap = await fetchSnap();
        if (snap?.outcome) return { kind: 'ended', snap };
        if (snap) last = snap;
        // A null with no earlier snapshot can be a wedged page rather than eviction —
        // keep polling; null after a live read means the trace was dropped.
        else if (last) return { kind: 'gone', last };
        if (waited + pollMs > maxMs) return last ? { kind: 'still-open', last } : { kind: 'gone', last: null };
        await sleep(pollMs);
    }
}

/** One durable log line for the outcome. Media failure detail is limited to the
 *  sanitized codes (never the error message text). */
export function formatLatePageOutcome(outcome: LatePageOutcome, guardTimeoutMs: number): string {
    const head = `Late page outcome after ${guardTimeoutMs}ms timeout`;
    if (outcome.kind !== 'ended') {
        return `${head} — ${outcome.kind} trace: ${formatSendTrace(outcome.last)}`;
    }
    const { snap } = outcome;
    const media = snap.media;
    const mediaBits = media
        ? [
            media.site && `site=${media.site}`,
            media.failStage && `failStage=${media.failStage}`,
            media.failCode && `failCode=${media.failCode}`,
            media.failAck !== undefined && `failAck=${media.failAck}`,
            media.errorName && `errorName=${media.errorName}`,
        ].filter((bit): bit is string => typeof bit === 'string')
        : [];
    const detail = [`pageTotalMs=${snap.totalMs}`, ...mediaBits].join(' ');
    return `${head} — ${snap.outcome} (${detail}) trace: ${formatSendTrace(snap)}`;
}
